import { createTheme, type ThemeOptions } from '@mui/material/styles';

/**
 * Kyykkä Tournament Theme
 * 
 * Colors inspired by TEA Club branding:
 * - Primary: Magenta (#f43494) from teaclub.fi
 * - Secondary: Warm orange (energy/action)
 * - Dark mode optimized for outdoor visibility
 */

const baseTheme: ThemeOptions = {
  typography: {
    fontFamily: [
      'Inter',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    h1: {
      fontWeight: 700,
    },
    h2: {
      fontWeight: 600,
    },
    h3: {
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12)',
        },
      },
    },
  },
};

export const lightTheme = createTheme({
  ...baseTheme,
  palette: {
    mode: 'light',
    primary: {
      main: '#f43494', // TEA Club magenta
      light: '#f85eb0',
      dark: '#c4207a',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#F57C00', // Warm orange
      light: '#FFB74D',
      dark: '#E65100',
      contrastText: '#FFFFFF',
    },
    error: {
      main: '#D32F2F',
    },
    warning: {
      main: '#FFA000',
    },
    success: {
      main: '#388E3C',
    },
    background: {
      default: '#F5F7FA',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#1A1A2E',
      secondary: '#4A4A68',
    },
  },
});

export const darkTheme = createTheme({
  ...baseTheme,
  palette: {
    mode: 'dark',
    primary: {
      main: '#f85eb0', // Lighter magenta for dark mode
      light: '#ffb3d9',
      dark: '#f43494',
      contrastText: '#000000',
    },
    secondary: {
      main: '#FFB74D', // Lighter orange for dark mode
      light: '#FFE97D',
      dark: '#F57C00',
      contrastText: '#000000',
    },
    error: {
      main: '#EF5350',
    },
    warning: {
      main: '#FFB300',
    },
    success: {
      main: '#66BB6A',
    },
    background: {
      default: '#0D1117',
      paper: '#161B22',
    },
    text: {
      primary: '#E6EDF3',
      secondary: '#8B949E',
    },
  },
});

export type ThemeMode = 'light' | 'dark';

